import React, { useState } from "react";
import { TextMessage, AlbumMetadata } from "../../../types/chat";
import MediaPreviewModal, { MediaItem } from "../../MediaPreviewModal";
import FormattedText from "../FormattedText";
import styles from "./AlbumMessage.module.scss";

interface AlbumMessageProps {
  message: TextMessage;
  metadata: AlbumMetadata;
  isOwnMessage?: boolean;
}

const AlbumMessage: React.FC<AlbumMessageProps> = ({ message, metadata, isOwnMessage }) => {
  // Split URLs bằng dấu chấm phẩy với null check
  const urls = metadata.urls?.split(";").filter((url) => url.trim()) || [];
  const thumbs = metadata.thumbs?.split(";").filter((url) => url.trim()) || [];

  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleImageLoad = (index: number) => {
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
    setShowPreview(true);
  };

  // Tạo danh sách preview items từ album
  const previewItems: MediaItem[] = urls.map((url, index) => ({
    type: "image" as const,
    url: url,
    thumbnailUrl: thumbs[index],
    name: `${metadata.name || "Album"} - Ảnh ${index + 1}`,
    caption:
      index === 0 && message.content?.message && !urls.includes(message.content.message)
        ? message.content.message
        : undefined,
  }));

  // Zalo-style: 2 columns, last image spans 2 if odd count
  const imageCount = urls.length;
  const isOddCount = imageCount % 2 === 1;

  return (
    <>
      <div className={styles.albumMessage}>
        <div className={styles.albumGrid}>
          {urls.map((url, index) => {
            const isLastImage = index === imageCount - 1;
            const shouldSpan = isOddCount && isLastImage;

            return (
              <div
                key={index}
                className={`${styles.albumItem} ${shouldSpan ? styles.spanTwo : ""}`}
                onClick={() => handleImageClick(index)}
                style={{ cursor: "pointer" }}
              >
                {!loadedImages.has(index) && <div className={styles.imagePlaceholder}>Đang tải...</div>}
                <img
                  src={thumbs[index] || url}
                  alt={`Album ${index + 1}`}
                  className={styles.albumImage}
                  onLoad={() => handleImageLoad(index)}
                  style={{ display: loadedImages.has(index) ? "block" : "none" }}
                />
                {/* HD Badge */}
                <div className={styles.hdBadge}>HD</div>
              </div>
            );
          })}
        </div>

        {/* Display text content if exists */}
        {message.content?.message && !urls.includes(message.content.message) && (
          <div className={`${styles.albumCaption} ${isOwnMessage ? styles.ownMessage : styles.otherMessage}`}>
            <FormattedText text={message.content.message} />
          </div>
        )}
      </div>

      <MediaPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        items={previewItems}
        initialIndex={selectedIndex}
      />
    </>
  );
};

export default AlbumMessage;
